/**
 * @file freeMinutes.ts
 * @brief The Free plan's 600 Meeting Notes minutes on web and Mac (B4/B5,
 *        22-10-2026): the usage line, the calm 80% notice, the used-up
 *        state, the verify-email gate, and the recorder's live usage signal.
 *
 * @details
 * Free has no card and no overage (DES 22-10-2026): at the limit a NEW
 * recording is refused (402 `FREE_LIMIT_REACHED`), a running one is never
 * cut. Every number is the server's (`freeMinutes` on GET
 * /billing/subscriptions, `GET /billing/usage/live`); nothing is re-derived
 * beyond capping `used` at `limit` for display. Web may offer Pro; the phone
 * never does (ADR-043), so the Pro words live here, not in the server text.
 * Pure (relative imports only) so node:test can load it.
 *
 * Fair use (B6, 22-10-2026): Pro / Business Meeting Notes stop NEW
 * recordings at the plan month's fair use (`fairUse`, 60 h); same pattern
 * as Free, but nothing shows below 80% and there is nothing to sell: the
 * server's sentence, no card, no upgrade.
 *
 * One-time Free (B20 / W17, 24-10-2026): when `resetMode` is "one_time" the
 * minutes and Asks are a total, once: "220 of 300 free minutes left" with
 * "No expiry" or "Ends 23 Nov", never "this month" or a reset day. Used up
 * or ended is not a dead end: the next recording is pay as you go with a card
 * (the server's hold decides), so the web doesn't block the start itself.
 * Monthly keeps today's words ("resets 1 November"). A backend before B20
 * sends no `resetMode`: monthly. Read `nextResetAt`, not `resetsAt` (which
 * stays the next 1st for old builds). Same words as the phone (P9).
 *
 * B43 (31-10-2026, DES + investor, ADR-061): Free is never charged per use.
 * ONE pool ("300 free minutes for Meeting Notes or Full Intelligence"), no
 * pay as you go after it: used up = "Pick a plan" (web + Mac only). Super
 * Admin may cap the Full part (FREE_INTEL_MAX_MINUTES): then "Full
 * Intelligence is on Pro. Pick a plan." A live recording warns at 5 free
 * minutes left and stops 15 minutes after them (the recorder ends it; what
 * was recorded is saved). Free minutes are once per device / email: a
 * refused grant shows the server's sentence and 0 minutes.
 *
 * Related: ./components/FreeMinutesNotice.tsx,
 *          ../employee-boardroom/useLiveUsage.ts
 *
 * @author DES
 * @date 22-10-2026
 */

/** `freeMinutes` on GET /billing/subscriptions and /billing/usage/live (B4). */
export type FreeMinutes = {
  used: number;
  limit: number;
  resetsAt: string;
  blocked: boolean;
  emailVerified: boolean;
  // B20 (24-10-2026), absent on an older backend.
  resetMode?: string | null;
  totalMinutes?: number | null;
  usedMinutes?: number | null;
  remainingMinutes?: number | null;
  asksTotal?: number | null;
  asksUsed?: number | null;
  /** null = never. */
  expiresAt?: string | null;
  expired?: boolean | null;
  /** null in one-time mode. */
  nextResetAt?: string | null;
  /** B43: always false (Free is never pay as you go). */
  payAsYouGoAfter?: boolean | null;
  // B43 (31-10-2026): one pool for Meeting Notes or Full Intelligence.
  poolMinutesLeft?: number | null;
  intelMaxMinutes?: number | null;
  intelCapped?: boolean | null;
  intelUsedMinutes?: number | null;
  intelRemainingMinutes?: number | null;
  graceMinutes?: number | null;
  warnAtMinutesLeft?: number | null;
  /** Free minutes refused once per device / disposable email (the server's sentence). */
  grantRefused?: { reason: string; message: string } | null;
};

/** One-time Free (B20): the numbers are a total, once. */
export function isOneTime(fm: Pick<FreeMinutes, "resetMode"> | null | undefined): boolean {
  return fm?.resetMode === "one_time";
}

/** "23 Nov": the short expiry day (UTC, as the server counts). */
export function shortDayLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(d);
}

/** "No expiry" / "Ends 23 Nov" / "Ended 23 Nov" for one-time Free; null otherwise. */
export function freeExpiryText(fm: FreeMinutes | null | undefined): string | null {
  if (!isOneTime(fm)) return null;
  if (!fm?.expiresAt) return "No expiry";
  const day = shortDayLabel(fm.expiresAt);
  if (!day) return null;
  return fm.expired ? `Ended ${day}` : `Ends ${day}`;
}

/** One-time used up (B20; B43: never pay as you go). The button says "Pick a plan". */
export const FREE_USED_UP_ONCE_MESSAGE = "You've used your free minutes.";
/** One-time ended by its expiry (B20). */
export const FREE_ENDED_MESSAGE = "Your free minutes have ended.";
/** B43: "300 free minutes for Meeting Notes or Full Intelligence" (DES 31-10). */
export const FREE_POOL_FOR = "for Meeting Notes or Full Intelligence";
/** B43: Super Admin capped Full Intelligence and it is used (DES 31-10, web + Mac). */
export const FREE_INTEL_ON_PRO_MESSAGE = "Full Intelligence is on Pro. Pick a plan.";
/** B43: the recorder ended a Free recording at the end of the grace. */
export const FREE_RECORDING_STOPPED_MESSAGE = "Your free minutes have run out. What you recorded is saved and is being processed.";

export type FreeMinutesState = "verify" | "blocked" | "warn" | "ok";

/** At this share of the limit a calm notice shows (the server's 80% push). */
export const FREE_WARN_FRACTION = 0.8;

/** The used-up sentence on web and Mac (W7): the reason and the way out. */
export const FREE_USED_UP_MESSAGE = "You've used your free minutes this month. Pro has unlimited Meeting Notes.";

/** "1 November": the reset day, in UTC like the server's month. */
export function resetDayLabel(resetsAt: string | null | undefined): string | null {
  if (!resetsAt) return null;
  const d = new Date(resetsAt);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", timeZone: "UTC" }).format(d);
}

const fmt = (n: number) => Math.round(n).toLocaleString("en-GB");

/** One-time: total, used and what is left, from the B20 fields (old ones as fallback). */
function onceNumbers(fm: FreeMinutes) {
  const total = fm.totalMinutes ?? fm.limit;
  const used = fm.usedMinutes ?? fm.used;
  const left = fm.expired ? 0 : Math.max(0, fm.remainingMinutes ?? total - used);
  return { total, used, left };
}

/**
 * Monthly: "480 of 600 free minutes used · resets 1 November".
 * One-time (B20): "220 of 300 free minutes left · No expiry" / "· Ends 23 Nov";
 * ended: "Your free minutes have ended".
 */
export function freeMinutesLine(fm: FreeMinutes | null | undefined): string | null {
  if (fm?.grantRefused) return fm.grantRefused.message;
  if (fm && isOneTime(fm)) {
    if (fm.expired) return "Your free minutes have ended";
    const { total, left } = onceNumbers(fm);
    if (!Number.isFinite(total) || total <= 0) return null;
    const expiry = freeExpiryText(fm);
    return `${fmt(Math.min(left, total))} of ${fmt(total)} free minutes left${expiry ? ` · ${expiry}` : ""}`;
  }
  if (!fm || !Number.isFinite(fm.limit) || fm.limit <= 0) return null;
  const used = Math.min(Math.max(0, fm.used), fm.limit);
  const day = resetDayLabel(fm.resetsAt);
  return `${fmt(used)} of ${fmt(fm.limit)} free minutes used${day ? ` · resets ${day}` : ""}`;
}

/**
 * What the Free user sees first: confirm the email (no recording can start
 * yet), used up, the 80% notice, or just the line.
 */
export function freeMinutesState(fm: FreeMinutes | null | undefined): FreeMinutesState | null {
  if (!fm) return null;
  if (!fm.emailVerified) return "verify";
  if (fm.grantRefused) return "blocked";
  if (isOneTime(fm)) {
    const { total, left } = onceNumbers(fm);
    if (fm.expired || left <= 0) return "blocked";
    if (total > 0 && left <= total * (1 - FREE_WARN_FRACTION)) return "warn";
    return "ok";
  }
  if (fm.blocked || fm.used >= fm.limit) return "blocked";
  if (fm.limit > 0 && fm.used >= fm.limit * FREE_WARN_FRACTION) return "warn";
  return "ok";
}

/** The calm 80% sentence. */
export function freeWarnMessage(fm: FreeMinutes): string {
  if (isOneTime(fm)) {
    const { left } = onceNumbers(fm);
    return `${fmt(left)} free minutes left. They don't reset.`;
  }
  const left = Math.max(0, fm.limit - fm.used);
  const day = resetDayLabel(fm.resetsAt);
  return `${fmt(left)} free minutes left this month${day ? `; they reset on ${day}` : ""}.`;
}

/** The second sentence when used up: when recording works again. */
export function freeResetsMessage(fm: FreeMinutes | null | undefined): string | null {
  if (!fm || isOneTime(fm)) return null;
  const day = resetDayLabel(fm.resetsAt);
  return day ? `New recordings start again on ${day}.` : null;
}

/** The used-up sentence for this Free mode. */
export function freeUsedUpMessage(fm: FreeMinutes | null | undefined): string {
  if (fm?.grantRefused) return fm.grantRefused.message;
  if (!isOneTime(fm)) return FREE_USED_UP_MESSAGE;
  return fm?.expired ? FREE_ENDED_MESSAGE : FREE_USED_UP_ONCE_MESSAGE;
}

/**
 * True when a new Free recording must not start (web gate). B43: no pay as
 * you go after the pool, so used up blocks Meeting Notes AND Full
 * Intelligence; a Full start also stops at FREE_INTEL_MAX_MINUTES.
 */
export function freeStartBlocked(fm: FreeMinutes | null | undefined, tier: "notes" | "full" = "notes"): boolean {
  const s = freeMinutesState(fm);
  if (s === "blocked" || s === "verify") return true;
  return tier === "full" && freeIntelUsedUp(fm);
}

/** B43: "300 free minutes for Meeting Notes or Full Intelligence", or null. */
export function freePoolTitle(fm: FreeMinutes | null | undefined): string | null {
  if (!fm || fm.grantRefused) return null;
  const total = fm.totalMinutes ?? fm.limit;
  if (!Number.isFinite(total) || total <= 0) return null;
  return `${fmt(total)} free minutes ${FREE_POOL_FOR}`;
}

/** B43: Super Admin capped Full Intelligence and this user's Full part is used. */
export function freeIntelUsedUp(fm: FreeMinutes | null | undefined): boolean {
  if (!fm || !fm.intelCapped) return false;
  return (fm.intelRemainingMinutes ?? 1) <= 0 && (fm.poolMinutesLeft ?? fm.remainingMinutes ?? 0) > 0;
}

// ------------------------------------------------------------ Fair use (B6)

/** `fairUse` on GET /billing/subscriptions and /billing/usage/live (B6). */
export type FairUse = {
  used: number;
  limit: number;
  resetsAt: string;
  blocked: boolean;
};

export type FairUseState = "blocked" | "warn";

/** "60 hours", "1 hour", "7.5 hours": the server's hours label. */
export function hoursLabel(minutes: number): string {
  const h = Math.round((minutes / 60) * 10) / 10;
  return `${h.toLocaleString("en-GB")} ${h === 1 ? "hour" : "hours"}`;
}

/** What a plan holder sees: nothing under 80%, then the line, then used up. */
export function fairUseState(fu: FairUse | null | undefined): FairUseState | null {
  if (!fu || !Number.isFinite(fu.limit) || fu.limit <= 0) return null;
  if (fu.blocked || fu.used >= fu.limit) return "blocked";
  if (fu.used >= fu.limit * FREE_WARN_FRACTION) return "warn";
  return null;
}

/** "52 of 60 hours": whole hours used (capped at the limit) of the limit. */
export function fairUseHours(fu: FairUse | null | undefined): string | null {
  if (!fu || !Number.isFinite(fu.limit) || fu.limit <= 0) return null;
  const used = Math.floor(Math.min(Math.max(0, fu.used), fu.limit) / 60);
  return `${fmt(used)} of ${hoursLabel(fu.limit)}`;
}

/** "Meeting Notes: 52 of 60 hours this month · resets 15 November". */
export function fairUseLine(fu: FairUse | null | undefined): string | null {
  const hours = fairUseHours(fu);
  if (!fu || !hours) return null;
  const day = resetDayLabel(fu.resetsAt);
  return `Meeting Notes: ${hours} this month${day ? ` · resets ${day}` : ""}`;
}

/** The used-up sentence, as the server's 402 says it (no price, no upgrade). */
export function fairUseReachedMessage(fu: FairUse): string {
  const day = resetDayLabel(fu.resetsAt);
  return `You've used this month's ${hoursLabel(fu.limit)} of Meeting Notes.${day ? ` They reset on ${day}.` : ""}`;
}

/** True when a new Meeting Notes recording must not start. Full Intelligence is never gated. */
export function fairUseStartBlocked(fu: FairUse | null | undefined): boolean {
  return fairUseState(fu) === "blocked";
}

// ---------------------------------------------------------------- Live (B5)

export type LiveUsageEvent = {
  type: string;
  atElapsedMinutes?: number | null;
  message?: string | null;
  priceNote?: string | null;
  /** B6: this recording reached fair use; the next Notes start is refused. */
  nextRecordingBlocked?: boolean | null;
};

/** The parts of GET /billing/usage/live the recorder reads. */
export type LiveUsage = {
  includedMinutesLeft: number | null;
  /** "notes_fair_use" on a Pro / Business Notes recording (B6). */
  pool?: string | null;
  fairUse?: FairUse | null;
  includedMinutesLeftAtStart?: number | null;
  events?: LiveUsageEvent[] | null;
  pollSeconds?: number | null;
};

/** "412 included minutes left", or null (pay as you go: no included minutes). */
export function includedLeftLine(live: LiveUsage | null | undefined): string | null {
  // Fair use (B6) stays out of sight under 80%: "3,540 minutes left" on a
  // plan with unlimited Notes would read like a meter.
  if (live?.pool === "notes_fair_use" && !fairUseState(live.fairUse)) return null;
  const left = live?.includedMinutesLeft;
  if (left == null || !Number.isFinite(left)) return null;
  const n = Math.max(0, Math.floor(left));
  return `${fmt(n)} included minute${n === 1 ? "" : "s"} left`;
}

/**
 * The one-time sentence when this recording used up the included minutes,
 * or null. Web may add the price note (the phone may not, ADR-043).
 */
export function includedUsedNotice(live: LiveUsage | null | undefined): string | null {
  const ev = live?.events?.find((e) => e?.type === "included_minutes_used");
  if (!ev?.message) return null;
  return ev.priceNote ? `${ev.message} ${ev.priceNote}` : ev.message;
}

/** Poll interval in ms: the server's, at least 15 s, 60 s by default. */
export function livePollMs(live: LiveUsage | null | undefined): number {
  const s = live?.pollSeconds;
  return Math.max(15, s != null && Number.isFinite(s) && s > 0 ? s : 60) * 1000;
}
